import { useMemo } from "react";
import { GridColDef, GridRenderCellParams, GridColumnGroupingModel } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { ChallengeTableChallenge, ChallengeTableProgramVersionItem, ChallengeTableInspectionsProgramsVersionsItem } from "../../../api/types/dtos/challenge/ChallengeDto";
import ChallengeCell from "./ChallengesDetailedTable.cell";
import styles from "../ChallengesDetailedTable.module.scss";

export interface BuildGridParams {
  data?: {
    challenges: ChallengeTableChallenge[];
    programVersionItems: ChallengeTableProgramVersionItem[];
  };
  handleOpenRowModal: (row: any) => void;
}

export const useChallengesGridBuilder = (
  { data, handleOpenRowModal }: BuildGridParams
) => {
  // Compute all values first (no hooks yet)
  const programVersionItems = data?.programVersionItems || [];
  const challenges = data?.challenges || [];

  const challengeMap = new Map<string, ChallengeTableChallenge>();
  challenges.forEach((challenge) => {
    const existingChallenge = challengeMap.get(challenge.inspectedOrgId);
    if (existingChallenge) {
      existingChallenge.inspectionsProgramsVersionsItems = [
        ...existingChallenge.inspectionsProgramsVersionsItems,
        ...challenge.inspectionsProgramsVersionsItems,
      ];
    } else {
      challengeMap.set(challenge.inspectedOrgId, {
        ...challenge,
        inspectionsProgramsVersionsItems: [
          ...challenge.inspectionsProgramsVersionsItems,
        ],
      });
    }
  });
  const mergedChallenges = Array.from(challengeMap.values());

  const groupedPvis: Record<
    string,
    { name: string; items: ChallengeTableProgramVersionItem[] }
  > = {};
  programVersionItems.forEach((pvi) => {
    const levelId = pvi.programLevelId || "ungrouped";
    const levelName = pvi.programLevelName || "ungrouped";
    if (!groupedPvis[levelId]) {
      groupedPvis[levelId] = { name: levelName, items: [] };
    }
    groupedPvis[levelId].items.push(pvi);
  });

  for (const levelId in groupedPvis) {
    groupedPvis[levelId].items.sort((a, b) => {
      const cpA = a.cpNumber || "";
      const cpB = b.cpNumber || "";
      return cpA.localeCompare(cpB, undefined, { numeric: true });
    });
  }

  const sortedLevels = Object.entries(groupedPvis).sort(
    ([, groupA], [, groupB]) => groupA.name.localeCompare(groupB.name),
  );

  const fixedCols: GridColDef[] = [
    {
      field: "inspectedOrgName",
      headerName: "Finca",
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <span
          className={styles.frozenCellClickable}
          role="button"
          tabIndex={0}
          aria-label={`Ver detalles de ${params.value}`}
          onClick={() => handleOpenRowModal(params.row)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleOpenRowModal(params.row);
            }
          }}
        >
          {params.value}
        </span>
      ),
    },
    { field: "inspectedUserName", headerName: "Productor", width: 150 },
    {
      field: "inspectedUserPhones",
      headerName: "Teléfonos",
      width: 150,
      renderCell: (params: GridRenderCellParams<any, any, any>) => {
        const phones = params.row.inspectedUserPhones as string[] | undefined;
        return phones ? phones.join(", ") : "";
      },
    },
    {
      field: "inspectedOrgGroupsName",
      headerName: "Grupos",
      width: 180,
      renderCell: (params: GridRenderCellParams<any, any, any>) => {
        const groups = params.row.inspectedOrgGroupsName as
          | string[]
          | undefined;
        return groups ? groups.join(", ") : "";
      },
    },
    { field: "inspectedOrgRegionName", headerName: "Región", width: 150 },
    { field: "inspectedOrgCityName", headerName: "Ciudad", width: 150 },
    { field: "inspectedOrgVeredaName", headerName: "Vereda", width: 150 },
    { field: "inspectedUserId", headerName: "Cédula", width: 130 },
  ];

  const dynamicCols: GridColDef[] = [];
  const colGroupModel: GridColumnGroupingModel = [];

  sortedLevels.forEach(([levelId, group]) => {
    const groupChildren = group.items.map((pvi) => ({
      field: `pvi_${pvi.id}`,
    }));
    colGroupModel.push({
      groupId: `level_${levelId}`,
      headerName: group.name,
      children: groupChildren,
      renderHeaderGroup: () => (
        <Box sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{group.name}</Box>
      ),
    });

    group.items.forEach((pvi) => {
      dynamicCols.push({
        field: `pvi_${pvi.id}`,
        headerName: pvi.cpNumber,
        width: 80,
        align: "center",
        headerAlign: "center",
        sortable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Tooltip
            title={pvi.name || `Item ${pvi.cpNumber}`}
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  maxWidth: 400,
                  textAlign: "center",
                  padding: "8px 12px",
                },
              },
            }}
          >
            <Box sx={{ cursor: "help", fontWeight: "bold" }}>
              {pvi.cpNumber}
            </Box>
          </Tooltip>
        ),
        renderCell: (
          params: GridRenderCellParams<
            any,
            ChallengeTableInspectionsProgramsVersionsItem | undefined
          >,
        ) => {
          const item = params.value;
          if (!item) {
            return (
              <span style={{ color: "red", fontWeight: 600 }}>
                [No item for programVersionId: {pvi.id}]
              </span>
            );
          }
          return (
            <ChallengeCell
              item={item}
              rowId={params.row.id}
              pviId={pvi.id}
              cpNumber={pvi.cpNumber || ""}
              measurementTemplate={pvi.measurementTemplate}
            />
          );
        },
      });
    });
  });

  const gridRows = mergedChallenges.map((challenge, index) => {
    const row: any = {
      id: challenge.inspectedOrgId,
      inspectedOrgName: challenge.inspectedOrgName,
      inspectedUserName: challenge.inspectedUserName,
      inspectedUserPhones: challenge.inspectedUserPhones,
      inspectedOrgGroupsName: challenge.inspectedOrgGroupsName,
      inspectedOrgRegionName: challenge.inspectedOrgRegionName,
      inspectedOrgCityName: challenge.inspectedOrgCityName,
      inspectedOrgVeredaName: challenge.inspectedOrgVeredaName,
      inspectedUserId: challenge.inspectedUserId,
      _challengeData: challenge,
      _rowIndex: index,
      _isEven: index % 2 === 0,
    };
    programVersionItems.forEach((pvi) => {
      const inspectionItem = challenge.inspectionsProgramsVersionsItems.find(
        (inspItem) => inspItem.programVersionItemId === pvi.id,
      );
      row[`pvi_${pvi.id}`] = inspectionItem;
    });
    return row;
  });

  const allColumns = [...fixedCols, ...dynamicCols];

  // Always call useMemo in the same position (no early returns before this)
  return useMemo(() => ({
    columns: allColumns,
    rows: gridRows,
    columnGroupingModel: colGroupModel,
  }), [allColumns, gridRows, colGroupModel]);
};
